<section class="notifications-page">
  <header class="page-header">
    <div class="header-title">
      <div class="header-icon" aria-hidden="true">
        <ng-icon name="bellNotification" size="24"></ng-icon>
      </div>
      <div>
        <h5>{{ dictionaryService.get('CreateNotification') }}</h5>
        <p>{{ dictionaryService.get('CreateNotificationHint') }}</p>
      </div>
    </div>

    <button
      class="help-card"
      type="button"
      [attr.aria-label]="dictionaryService.get('NotificationHelpTitle')"
      (click)="openHelpModal()"
    >
      <span class="help-icon" aria-hidden="true">
        <ng-icon name="helpCircle" size="20"></ng-icon>
      </span>
      <span class="help-card__copy">
        <strong>{{ dictionaryService.get('NotificationHelpTitle') }}</strong>
        <span>{{ dictionaryService.get('NotificationHelpSubtitle') }}</span>
      </span>
      <ng-icon name="navArrowRight" size="18"></ng-icon>
    </button>
  </header>

  @if (loading()) {
    <app-loading-spinner
      class="page-loader"
      size="md"
      [label]="dictionaryService.get('LoadingNotificationsData')"
    ></app-loading-spinner>
  } @else if (loadError()) {
    <div class="state-panel error-state">
      <p>{{ loadError() }}</p>
      <button
        buttonTheme="light"
        buttonType="regular"
        type="button"
        (click)="loadPageData()"
      >
        {{ dictionaryService.get('Retry') }}
      </button>
    </div>
  } @else {
    <form
      [formGroup]="notificationForm"
      (ngSubmit)="submit()"
      class="notification-form"
    >
      <div class="input-group">
        <label for="notification-title">
          {{ dictionaryService.get('NotificationTitle') }} *
        </label>
        <div class="input-wrapper">
          <app-input
            id="notification-title"
            type="text"
            size="lg"
            iconName="pageEdit"
            iconColor="var(--jellyfish-600)"
            formControlName="title"
            [placeholder]="dictionaryService.get('EnterNotificationTitle')"
            [isError]="f.title.invalid && (f.title.dirty || f.title.touched)"
          ></app-input>

          @if (f.title.invalid && (f.title.dirty || f.title.touched)) {
            <small class="error-msg">
              {{ dictionaryService.get('NotificationTitleMissing') }}
            </small>
          }
        </div>
      </div>

      <div class="input-group">
        <label for="notification-body">
          {{ dictionaryService.get('NotificationBody') }} *
        </label>
        <div class="input-wrapper">
          <app-input
            id="notification-body"
            type="textarea"
            size="lg"
            iconName="multiBubble"
            iconColor="var(--jellyfish-600)"
            formControlName="body"
            [textAreaRows]="7"
            [placeholder]="dictionaryService.get('EnterNotificationBody')"
            [isError]="f.body.invalid && (f.body.dirty || f.body.touched)"
          ></app-input>

          @if (f.body.invalid && (f.body.dirty || f.body.touched)) {
            <small class="error-msg">
              {{ dictionaryService.get('NotificationBodyMissing') }}
            </small>
          }
        </div>
      </div>

      <div class="row-group">
        <div class="input-group">
          <label>{{ dictionaryService.get('Application') }} *</label>
          <div class="input-wrapper">
            <app-select
              #applicationSelect
              size="sm"
              [options]="applications()"
              [disableSearch]="true"
              [isFullWidth]="true"
              [placeholder]="dictionaryService.get('SelectApplication')"
              [class]="isRequiredSelectInvalid(f.application.value, 'application') ? 'error-outline' : ''"
              (getSelectedOption)="onApplicationChange($event)"
              (focusout)="markSelectTouched('application', $event)"
            ></app-select>

            @if (isRequiredSelectInvalid(f.application.value, 'application')) {
              <small class="error-msg">
                {{ dictionaryService.get('ApplicationMissing') }}
              </small>
            }
          </div>
        </div>

        <div class="input-group">
          <label>{{ dictionaryService.get('NotificationType') }} *</label>
          <div class="input-wrapper">
            <app-select
              #typeSelect
              size="sm"
              [options]="notificationTypes()"
              [disableSearch]="true"
              [isFullWidth]="true"
              [placeholder]="dictionaryService.get('SelectNotificationType')"
              [class]="isRequiredSelectInvalid(f.notificationType.value, 'notificationType') ? 'error-outline' : ''"
              (getSelectedOption)="onNotificationTypeChange($event)"
              (focusout)="markSelectTouched('notificationType', $event)"
            ></app-select>

            @if (isRequiredSelectInvalid(f.notificationType.value, 'notificationType')) {
              <small class="error-msg">
                {{ dictionaryService.get('NotificationTypeMissing') }}
              </small>
            }
          </div>
        </div>
      </div>

      @if (recipientsRequired()) {
        <section class="recipient-panel">
          <div class="recipient-panel__header">
            <span class="recipient-panel__icon" aria-hidden="true">
              <ng-icon name="group" size="20"></ng-icon>
            </span>
            <div>
              <label>{{ recipientLabel() }}</label>
              <p>{{ recipientPlaceholder() }}</p>
            </div>
          </div>

          <div class="input-wrapper">
            <app-select
              #recipientsSelect
              type="check"
              size="sm"
              [options]="recipientOptions()"
              [optionsIds]="recipientIds()"
              [name]="recipientPlaceholder()"
              [placeholder]="recipientPlaceholder()"
              [isFullWidth]="true"
              [allUncheckedInitially]="true"
              [disableSearch]="false"
              [class]="hasRecipientError() ? 'error-outline' : ''"
              (getCheckedItemsIds)="onRecipientsChange($event)"
              (focusout)="markSelectTouched('recipientIds', $event)"
            ></app-select>

            @if (hasRecipientError()) {
              <small class="error-msg">
                {{ dictionaryService.get('NotificationRecipientsMissing') }}
              </small>
            }
          </div>
        </section>
      }

      <div class="form-actions">
        <button
          buttonTheme="light"
          buttonType="regular"
          type="button"
          (click)="reset()"
          [disabled]="submitting()"
        >
          {{ dictionaryService.get('Cancel') }}
        </button>

        <button
          buttonTheme="primary"
          buttonType="regular"
          type="submit"
          [disabled]="submitting() || !canSubmit()"
        >
          <ng-icon name="send" size="18"></ng-icon>
          @if (submitting()) {
            {{ dictionaryService.get('SendingNotification') }}
          } @else {
            {{ dictionaryService.get('CreateNotification') }}
          }
        </button>
      </div>
    </form>
  }

  <app-modal
    size="md"
    [(opened)]="helpModalOpened"
    [title]="dictionaryService.get('NotificationHelpModalTitle')"
  >
    <div class="notification-help">
      <p class="notification-help__intro">
        {{ dictionaryService.get('NotificationHelpModalIntro') }}
      </p>

      <article class="notification-help__item">
        <span class="notification-help__icon" aria-hidden="true">
          <ng-icon name="bellNotification" size="20"></ng-icon>
        </span>
        <div>
          <h6>{{ dictionaryService.get('NotificationHelpAllTitle') }}</h6>
          <p>{{ dictionaryService.get('NotificationHelpAllDescription') }}</p>
        </div>
      </article>

      <article class="notification-help__item">
        <span class="notification-help__icon" aria-hidden="true">
          <ng-icon name="group" size="20"></ng-icon>
        </span>
        <div>
          <h6>{{ dictionaryService.get('NotificationHelpTeamTitle') }}</h6>
          <p>{{ dictionaryService.get('NotificationHelpTeamDescription') }}</p>
        </div>
      </article>

      <article class="notification-help__item">
        <span class="notification-help__icon" aria-hidden="true">
          <ng-icon name="user" size="20"></ng-icon>
        </span>
        <div>
          <h6>{{ dictionaryService.get('NotificationHelpIndividualsTitle') }}</h6>
          <p>{{ dictionaryService.get('NotificationHelpIndividualsDescription') }}</p>
        </div>
      </article>
    </div>
  </app-modal>
</section>
